import { useState, useMemo, Fragment, useCallback, useRef } from 'react';
import { DataGrid, TinyButton, StatusChip, DropdownFilter, DateFilter } from '@lendica/components';
import { ValueFormatterParams, ICellRendererParams, GridApi } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
    currencyFormatter,
    safeDateFormatter,
    InvoiceTableRowStatus,
    FilterConfig,
    createDateFilterConfig,
    createDropdownFilterConfig,
    createAdvancedDropdownFilterConfig,
    applyFiltersFromUrl,
} from '@lendica/utils';
import MenuList from '@mui/material/MenuList';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { alpha } from '@mui/material/styles';
import { BillTableRow, ActionType, NULL_VALUE_FALLBACK } from '../sharedTypes';
import { useActionsNew, ActionNew, ActionCallbacks } from '../hooks/useActions';

const VENDOR_FILTER_PARAM = 'vendor';
const VENDOR_BLANK_OPTION = 'Blank / Unknown';
const STATUS_FILTER_PARAM = 'status';
const DUE_DATE_FILTER_PARAM = 'dueDate';
const FUNDING_DATE_FILTER_PARAM = 'fundingDate';
const INVOICE_DATE_FILTER_PARAM = 'invoiceDate';
const STATUS_OPTIONS = [
    InvoiceTableRowStatus.Ready,
    InvoiceTableRowStatus.UnderReview,
    InvoiceTableRowStatus.Active,
    InvoiceTableRowStatus.GracePeriod,
    InvoiceTableRowStatus.Late,
    InvoiceTableRowStatus.Defaulted,
    InvoiceTableRowStatus.PaidOff,
    InvoiceTableRowStatus.Rejected,
];

const FILTER_CONFIGS: FilterConfig[] = [
    { param: STATUS_FILTER_PARAM, column: 'status', type: 'dropdown' },
    { param: VENDOR_FILTER_PARAM, column: 'vendor', type: 'dropdown' },
    { param: DUE_DATE_FILTER_PARAM, column: 'due_date', type: 'date' },
    { param: FUNDING_DATE_FILTER_PARAM, column: 'funding_date', type: 'date' },
    { param: INVOICE_DATE_FILTER_PARAM, column: 'invoice_date', type: 'date' }
];

export interface BillsTableProps {
    rowData: BillTableRow[];
    actionCallbacks?: ActionCallbacks;
    gridRef?: React.RefObject<AgGridReact>;
    onFilterChanged?: () => void;
    // When false, disables AG Grid's built-in pagination to support server-side/external controls
    useGridPagination?: boolean;
    paginationPageSize?: number;
}

export const BillsTable = ({
    rowData,
    actionCallbacks,
    gridRef,
    onFilterChanged,
    useGridPagination = true,
    paginationPageSize = 20,
}: BillsTableProps) => {
    const gridApiRef = useRef<GridApi | null>(null);

    // Generate vendor options from current rowData
    const vendorOptions = useMemo(() => {
        const uniqueVendors = new Set<string>();

        rowData.forEach(row => {
            if (row.vendor && row.vendor.trim() !== NULL_VALUE_FALLBACK) {
                uniqueVendors.add(row.vendor.trim());
            }
        });

        const sortedVendors = Array.from(uniqueVendors).sort();
        sortedVendors.push(VENDOR_BLANK_OPTION);

        return sortedVendors;
    }, [rowData]);

    const colDefs = useMemo(
        () => [
            {
                field: 'vendor',
                headerName: 'Vendor',
                suppressSpanHeaderHeight: true,
                pinned: 'left' as const,
                filter: createAdvancedDropdownFilterConfig({
                    filterParam: VENDOR_FILTER_PARAM,
                    options: vendorOptions,
                    enableSearch: true,
                    blankOption: VENDOR_BLANK_OPTION,
                    nullValueFallback: NULL_VALUE_FALLBACK,
                }, DropdownFilter),
            },
            {
                field: 'status',
                headerName: 'Status',
                type: ['status'],
                filter: createDropdownFilterConfig(STATUS_FILTER_PARAM, STATUS_OPTIONS, DropdownFilter),
                suppressSpanHeaderHeight: true,
            },

            // {
            //     field: 'added_date',
            //     headerName: 'Added date',
            //     type: ['date', 'rightAligned'],
            //     sort: 'desc',
            // },
            {
                headerName: 'Invoice details',
                groupId: 'invoiceDetails',
                children: [
                    { field: 'invoice_number', headerName: 'Invoice #' },
                    {
                        field: 'due_date',
                        headerName: 'Due date',
                        type: ['date', 'rightAligned'],
                        sort: 'asc' as const,
                        sortIndex: 1,
                        headerTooltip: 'The date the invoice is due',
                        filter: createDateFilterConfig(DUE_DATE_FILTER_PARAM, DateFilter),
                    },
                    {
                        field: 'invoice_date',
                        headerName: 'Issue date',
                        type: ['date', 'rightAligned'],
                        headerTooltip: 'The date the invoice was issued',
                        columnGroupShow: 'open',
                        filter: createDateFilterConfig(INVOICE_DATE_FILTER_PARAM, DateFilter),
                    },
                    {
                        field: 'amount',
                        headerName: 'Amount',
                        type: ['currency', 'rightAligned'],
                    },
                ],
            },
            {
                headerName: 'Payment details',
                groupId: 'paymentDetails',
                children: [
                    {
                        field: 'advance_amount',
                        headerName: 'Advance amount',
                        type: ['currency', 'rightAligned'],
                        headerTooltip:
                            'The amount financed by Lendica. The full invoice amount is paid to your vendor by Lendica',
                        columnGroupShow: 'open',
                    },
                    {
                        field: 'funding_date',
                        headerName: 'Funding date',
                        type: ['date', 'rightAligned'],
                        sort: 'desc' as const,
                        sortIndex: 0,
                        headerTooltip: 'The date the vendor is scheduled to be paid',
                        filter: createDateFilterConfig(FUNDING_DATE_FILTER_PARAM, DateFilter),
                    },
                    {
                        field: 'first_payment_date',
                        headerName: 'First repayment date',
                        type: ['date', 'rightAligned'],
                        headerTooltip:
                            'The date your first repayment installment to Lendica is due',
                    },
                    {
                        field: 'payment_frequency',
                        headerName: 'Repayment frequency',
                        type: 'paymentSchema',
                        columnGroupShow: 'open',
                        headerTooltip:
                            'The frequency of your repayment installments to Lendica, starting from the first repayment date',
                    },
                    {
                        field: 'number_of_payments',
                        headerName: 'No. of payments',
                        columnGroupShow: 'open',
                    },
                    {
                        field: 'total_collected',
                        headerName: 'Total repaid',
                        type: ['currency', 'rightAligned'],
                        headerTooltip: 'Total amount you have paid back to Lendica to date',
                        columnGroupShow: 'open',
                    },
                ],
            },
            {
                field: 'actions',
                headerName: 'Actions',
                type: ['actions', 'centerAligned'],
                pinned: 'right' as const,
                suppressHeaderFilterButton: true,
                suppressHeaderContextMenu: true,
                suppressHeaderMenuButton: true,
                suppressSpanHeaderHeight: true,
                cellStyle: {
                    padding: '0px',
                },
            },
        ],
        [vendorOptions]
    );

    const defaultColDef = useMemo(() => {
        return {
            resizable: true,
            filter: true,
        };
    }, []);

    const columnTypes = useMemo(() => {
        return {
            currency: {
                valueFormatter: (params: ValueFormatterParams) => {
                    const value = params.value;
                    // If value is the dash fallback or not a number, return as-is
                    if (typeof value !== 'number' || isNaN(value)) {
                        return value;
                    }
                    return currencyFormatter(value);
                },
            },
            date: {
                valueFormatter: (params: ValueFormatterParams) =>
                    safeDateFormatter(params.value as string, 'MMM d, yyyy'),
            },
            actions: {
                cellRenderer: (params: ICellRendererParams<BillTableRow>) => (
                    <ActionsCellRenderer params={params} actionCallbacks={actionCallbacks} />
                ),
            },
            status: {
                cellRenderer: (params: ICellRendererParams<BillTableRow>) => (
                    <StatusChip status={params.value} />
                ),
            },
            paymentSchema: {
                valueFormatter: (params: ValueFormatterParams) => {
                    const value = params.value;
                    if (value === 'W' || value === 'WEEKLY') {
                        return 'Weekly';
                    }
                    if (value === 'M' || value === 'MONTHLY') {
                        return 'Monthly';
                    }
                    if (value === 'B' || value === 'BULLET') {
                        return 'One-time';
                    }
                    if (value === 'D' || value === 'DAILY') {
                        return 'Daily';
                    }
                    return value;
                },
            },
        };
    }, []);

    const autoSizeStrategy = useMemo(() => {
        return {
            type: 'fitCellContents' as const,
        };
    }, []);

    const onGridReady = useCallback((params: any) => {
        gridApiRef.current = params.api;
        applyFiltersFromUrl(params.api, FILTER_CONFIGS);
    }, []);

    return (
        <DataGrid
            ref={gridRef}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            rowData={rowData}
            columnDefs={colDefs}
            autoSizeStrategy={autoSizeStrategy}
            pagination={useGridPagination}
            paginationPageSize={paginationPageSize}
            overlayNoRowsTemplate={`<span>Upload invoice files to get started.</span>`}
            onFilterChanged={onFilterChanged}
            onGridReady={onGridReady}
        />
    );
};

const ActionsCellRenderer = ({
    params,
    actionCallbacks,
}: {
    params: ICellRendererParams<BillTableRow>;
    actionCallbacks?: ActionCallbacks;
}) => {
    const row = params.data;
    const actions = useActionsNew(row, actionCallbacks);

    return (
        <div
            style={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                height: '100%',
            }}
        >
            {actions.map(action => {
                return <ActionButton key={action.type} action={action} />;
            })}
            <ContextMenu actions={actions} />
        </div>
    );
};

const ActionButton = ({ action }: { action: ActionNew }) => {
    switch (action.type) {
        case ActionType.Schedule:
            return (
                <TinyButton variant="contained" size="small" onClick={action.onClick}>
                    {action.label}
                </TinyButton>
            );
        case ActionType.Edit:
            return (
                <TinyButton
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={action.onClick}
                    sx={{
                        backgroundColor: alpha('#202024', 0.04),
                    }}
                >
                    {action.label}
                </TinyButton>
            );
        default:
            return null;
    }
};

const ContextMenu = ({ actions }: { actions: ActionNew[] }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    return (
        <Fragment>
            <IconButton
                size="small"
                color="info"
                sx={{
                    borderRadius: '2px',
                }}
                onClick={e => setAnchorEl(e.currentTarget)}
            >
                <MoreVertIcon fontSize="inherit" />
            </IconButton>
            <MenuList dense={true}>
                <Menu
                    variant="menu"
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    anchorEl={anchorEl}
                    sx={{
                        '& .MuiPaper-root': {
                            border: '1px solid #ddd',
                            backgroundColor: 'color-mix(in srgb, #FCFCFD, #202024 2%)',
                            fontSize: '12.5px',
                        },
                    }}
                >
                    {actions.map(action => {
                        return (
                            <div key={action.type}>
                                {action.type === ActionType.Archive && <Divider />}
                                <MenuItem
                                    onClick={() => {
                                        setAnchorEl(null);
                                        action.onClick();
                                    }}
                                >
                                    <ListItemIcon>
                                        <action.icon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>{action.label}</ListItemText>
                                </MenuItem>
                            </div>
                        );
                    })}
                </Menu>
            </MenuList>
        </Fragment>
    );
};
